import React, { useState } from "react";

import axios from "axios";

import Link from "@busbud/design-system-components/dist/Link/Link";
import Stack from "@busbud/design-system-components/dist/Stack/Stack";
import { Input } from "@busbud/horizon";
import { Button } from "@busbud/horizon";

import { NoDiacriticsInput } from "@app/components/diacritics";
import { isAxiosBusbudError } from "@app/helpers/api-error";
import { useAppContext } from "@app/helpers/hooks";
import type { NapiUser } from "@app/types/napi-types";

import type { AxiosResponse } from "axios";

import * as Validator from "../../../../helpers/input-validator";
import {
  getErrorText,
  isEmailFieldError,
  isPasswordFieldError
} from "../../../../modules/account/utils";
import { confirmSignIn } from "../../../../tracking/account-tracking";
import { failedRequest } from "../../../../tracking/error-tracking";
import { useAnimation } from "../authentication.styles";

interface Field {
  value: string;
  has_error?: boolean;
}

interface Props {
  setInProgress: (in_progress: boolean) => void;
  goToSignUp: (email?: string) => void;
  onSuccess: (user: NapiUser) => void;
  setError: (err: React.ReactNode) => void;
  goToResendAccountVerification: (email: string) => void;
}

export const SignInForm: React.FC<Props> = ({
  goToSignUp,
  goToResendAccountVerification,
  onSuccess,
  setError,
  setInProgress
}) => {
  const [submitted, setSubmitted] = useState(false);
  const { tracker, translator } = useAppContext();
  const [email, setEmail] = useState<Field>({ value: "", has_error: false });
  const [password, setPassword] = useState<Field>({
    value: "",
    has_error: false
  });
  const { classes } = useAnimation();

  const onResponseSuccess = (response: AxiosResponse<NapiUser>) => {
    setInProgress(false);
    setSubmitted(false);
    onSuccess(response.data);
  };

  const onError = (err: Error | null) => {
    setInProgress(false);
    setSubmitted(false);
    const error_type: string = getErrorType(err);
    tracker.track(failedRequest(err, "signin", { error_type }));

    if (error_type === "NoUser") {
      return goToSignUp(email.value);
    } else if (error_type === "UserNotVerified" && email) {
      setError(
        <>
          {translator.t("!account.verification.confirm-email-request")}
          <div>
            <Link onClick={() => goToResendAccountVerification(email.value)}>
              {translator.t("!account.verification.resend-verification-email")}
            </Link>
          </div>
        </>
      );
    } else {
      setError(getErrorText(translator, error_type));
    }

    setEmail({ ...email, has_error: isEmailFieldError(error_type) });
    setPassword({
      ...password,
      has_error: isPasswordFieldError(error_type)
    });
  };

  const getErrorType = (err: Error | null) => {
    if (!err || !isAxiosBusbudError(err)) {
      return undefined;
    }

    const { response } = err;
    return (
      response &&
      response.data &&
      response.data.error &&
      response.data.error.type
    );
  };

  const handleEmailSignIn = (event: React.MouseEvent) => {
    event.preventDefault();
    tracker.track(confirmSignIn());

    const email_error = Validator.email(email.value);
    if (email_error) {
      setEmail({ ...email, has_error: true });
      setError(getErrorText(translator, "email.invalid_email"));
      return;
    }
    const password_error = Validator.signinPassword(password.value);
    if (password_error) {
      setPassword({ ...password, has_error: true });
      setError(getErrorText(translator, "PasswordInvalid"));
      return;
    }

    setSubmitted(true);
    setError(undefined);
    setInProgress(true);
    axios
      .post<NapiUser>("/auth/signin", {
        email: email.value,
        password: password.value
      })
      .then(onResponseSuccess)
      .catch(onError);
  };

  return (
    <form autoComplete="on">
      <Stack spacing={2} direction="column" className={classes.container}>
        <Stack spacing={1} direction="column">
          <NoDiacriticsInput
            id="email"
            type="email"
            name="email"
            autoComplete="email"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- TODO: [fix-jsx-a11y]
            autoFocus
            className="w-full"
            labelText={translator.t("!account.signin.email")}
            value={email.value}
            isInvalid={email.has_error}
            isDisabled={submitted}
            onChange={e => setEmail({ value: e.target.value })}
          />
          <Input
            id="password"
            type="password"
            name="password"
            autoComplete="current-password"
            className="w-full"
            labelText={translator.t("!account.signin.password")}
            value={password.value}
            isInvalid={password.has_error}
            isDisabled={submitted}
            onChange={e => setPassword({ value: e.target.value })}
          />
        </Stack>
        <Button
          className="w-full rounded-xs"
          id="submit"
          isSubmit
          variant="contained"
          appearance="primary"
          size="lg"
          isDisabled={submitted}
          onClick={handleEmailSignIn}
        >
          {translator.t("!accounts.signup.login.link")}
        </Button>
      </Stack>
    </form>
  );
};
